import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import type {
  CategoriaGasto,
  FuenteGasto,
  ImpuestoDeGasto,
  QrDian,
  QrDianCampos,
  QrDianSoloUrl,
  TipoImpuestoGasto,
} from '@tanteo/compartido';
import {
  fechaNegocio,
  formatearCOP,
  formatearFecha,
  impuestoSinNombre,
  parsearQrDian,
  subtotalDeGasto,
  urlConsultaDian,
} from '@tanteo/compartido';
import {
  anularGastoRpc,
  diasCerrados,
  editarGastoRpc,
  listarGastos,
  registrarGastoRpc,
  type GastoResumen,
} from '../../lib/api-gastos';
import { listarCuentas, type Cuenta } from '../../lib/api-cuentas';
import { useApp } from '../../lib/contexto';
import { leerQrDeArchivo } from '../../lib/leer-qr-archivo';
import { leerDocumentoDeArchivo } from '../../lib/leer-documento-proveedor';
import { buscarProveedores, terceroPorDocumento, type TerceroEncontrado } from '../../lib/api-catalogo';
import { BarraDePagina, Boton, BotonEnlace, Campo, CampoLista, CampoNumero, CeldaPlata, EncabezadoTabla, Esqueleto, EstadoVacio, FilaAcciones, FilaCampos, FilaTabla, Insignia, MensajeError, TablaAncha, Tarjeta, ThAcciones } from '../../ui/base';
import { CampoDocumento } from '../../ui/CampoDocumento';
import { Modal } from '../../ui/Modal';
import { EscanerQr } from './EscanerQr';

const CATEGORIAS: { valor: CategoriaGasto; etiqueta: string }[] = [
  { valor: 'insumos', etiqueta: 'Insumos' },
  { valor: 'fijos', etiqueta: 'Fijos (arriendo, etc.)' },
  { valor: 'servicios', etiqueta: 'Servicios' },
  { valor: 'nomina', etiqueta: 'Nómina' },
  { valor: 'otros', etiqueta: 'Otros' },
];

/** Un tributo mientras se escribe: el monto puede quedar vacío, que no es lo mismo que cero. */
interface FilaImpuesto {
  tipo: TipoImpuestoGasto;
  nombre: string;
  codigoDian: string | null;
  tarifa: number | null;
  monto: number | null;
}

interface Borrador {
  fuente: FuenteGasto;
  cufe: string | null;
  qrCrudo: string | null;
  /** Lo que el documento leído dejó por decir: se muestra arriba del formulario. */
  nota: string | null;
  descripcion: string;
  categoria: CategoriaGasto;
  /** Lo que se pagó, como lo dice el recibo. El subtotal sale de restarle los impuestos. */
  total: number | null;
  conImpuestos: boolean;
  impuestos: FilaImpuesto[];
  /** El subtotal que declara el documento del proveedor, para avisar si no cuadra con la resta. */
  subtotalDocumento: number | null;
  /** De dónde salió la plata: el id de una cuenta, o `caja-general`. */
  origen: string;
  proveedorTipoDoc: string;
  nitEmisor: string;
  proveedorNombre: string;
  /** El proveedor ya existe con ese documento: su nombre manda y no se edita desde aquí. */
  proveedorRegistrado: boolean;
  /** Solo la que trae el documento del proveedor; un gasto a mano no tiene. */
  fechaDocumento: string | null;
}

const BORRADOR_MANUAL: Borrador = {
  fuente: 'manual', cufe: null, qrCrudo: null, nota: null, descripcion: '', categoria: 'insumos',
  total: null, conImpuestos: false, impuestos: [], subtotalDocumento: null, origen: '',
  proveedorTipoDoc: '31', nitEmisor: '', proveedorNombre: '', proveedorRegistrado: false,
  fechaDocumento: null,
};

const FILA_VACIA = (tipo: TipoImpuestoGasto): FilaImpuesto => ({
  tipo, nombre: '', codigoDian: null, tarifa: null, monto: null,
});

/**
 * Las filas del formulario como tributos del dominio: el nombre en blanco es «sin nombre» y la fila
 * cuyo monto nunca se escribió no se guarda —un cero declarado sí, que es un tributo exento—.
 */
function aImpuestos(filas: FilaImpuesto[]): ImpuestoDeGasto[] {
  return filas
    .filter((f) => f.monto !== null)
    .map((f) => ({
      tipo: f.tipo,
      nombre: f.nombre.trim() || null,
      codigoDian: f.codigoDian,
      tarifa: f.tarifa,
      monto: f.monto ?? 0,
    }));
}

/** El documento que se lee trae sus tributos; se marca la casilla para que se vean. */
function conFilas(b: Borrador, filas: FilaImpuesto[]): Borrador {
  return { ...b, impuestos: filas, conImpuestos: filas.length > 0 };
}

/**
 * Los tributos que trae el QR: el IVA de `ValIva` y, cuando viene con monto, `ValOtroIm`. El QR da la
 * cifra y no el nombre del tributo, así que se dice de dónde salió y la persona lo tipifica.
 */
function impuestosDelQr(parseado: QrDianCampos): FilaImpuesto[] {
  const filas: FilaImpuesto[] = [];
  if (parseado.valorIva) {
    filas.push({ tipo: 'iva', nombre: '', codigoDian: '01', tarifa: null, monto: parseado.valorIva });
  }
  if (parseado.valorOtrosImpuestos) {
    filas.push({
      tipo: 'otro', nombre: 'Otros impuestos (QR)', codigoDian: null, tarifa: null,
      monto: parseado.valorOtrosImpuestos,
    });
  }
  return filas;
}

/** Traduce el QR ya parseado a un borrador de gasto. */
function borradorDesdeQr(parseado: QrDianCampos | QrDianSoloUrl, crudo: string): Borrador {
  if (parseado.tipo === 'campos') {
    const filas = impuestosDelQr(parseado);
    const suma = filas.reduce((s, f) => s + (f.monto ?? 0), 0);
    return conFilas(
      {
        ...BORRADOR_MANUAL,
        fuente: 'qr',
        cufe: parseado.cufe,
        qrCrudo: crudo,
        nitEmisor: parseado.nitEmisor ?? '',
        fechaDocumento: parseado.fecha && /^\d{4}-\d{2}-\d{2}$/.test(parseado.fecha) ? parseado.fecha : null,
        subtotalDocumento: parseado.valorSinImpuestos ?? null,
        total: parseado.valorTotal ?? (parseado.valorSinImpuestos === undefined ? null : parseado.valorSinImpuestos + suma),
      },
      filas
    );
  }
  return { ...BORRADOR_MANUAL, fuente: 'qr', cufe: parseado.cufe, qrCrudo: crudo };
}

/** Un gasto guardado, tal como está, para corregirlo en el mismo formulario. */
function borradorDeGasto(g: GastoResumen): Borrador {
  return conFilas(
    {
      ...BORRADOR_MANUAL,
      fuente: g.fuente,
      cufe: g.cufe,
      descripcion: g.descripcion ?? '',
      categoria: g.categoria,
      total: g.total,
      origen: g.cajaGeneral ? 'caja-general' : (g.cuentaId ?? ''),
      proveedorTipoDoc: g.proveedor?.tipoDocumento ?? '31',
      nitEmisor: g.proveedor?.numeroDocumento ?? '',
      proveedorNombre: g.proveedor?.nombre ?? '',
      proveedorRegistrado: g.proveedor !== null,
    },
    g.impuestos.map((i) => ({ tipo: i.tipo, nombre: i.nombre ?? '', codigoDian: i.codigoDian, tarifa: i.tarifa, monto: i.monto }))
  );
}

/** Quién registró el gasto: el documento DIAN, la foto o la mano. */
function RegistradoPor({ g }: { g: GastoResumen }) {
  if (g.fuente === 'qr' && g.cufe) {
    return (
      <a href={urlConsultaDian(g.cufe)} target="_blank" rel="noreferrer">
        <Insignia tono="exito">QR ✓DIAN</Insignia>
      </a>
    );
  }
  return <Insignia tono="neutro">{g.fuente === 'qr' ? 'QR' : g.fuente === 'foto' ? 'Foto' : 'Manual'}</Insignia>;
}

/** Un bloque del formulario: la pregunta que responde, numerada para que se llene en orden. */
function Bloque({ numero, titulo, children }: { numero: number; titulo: string; children: ReactNode }) {
  return (
    <section className="space-y-2">
      <h3 className="text-muted text-etiqueta font-extrabold tracking-wide uppercase">
        {numero} · {titulo}
      </h3>
      {children}
    </section>
  );
}

export function Gastos() {
  const { negocio } = useApp();
  const [gastos, setGastos] = useState<GastoResumen[] | null>(null);
  const [errorLista, setErrorLista] = useState('');
  const [cerrados, setCerrados] = useState<Set<string>>(new Set());
  const [escaneando, setEscaneando] = useState(false);
  const [borrador, setBorrador] = useState<Borrador | null>(null);
  /** El gasto que se corrige; `null` cuando el formulario registra uno nuevo. */
  const [corrigiendo, setCorrigiendo] = useState<string | null>(null);
  /** Uno por borrador, como la venta del mostrador: un reintento tras un corte es el mismo gasto, no otro. */
  const [idGasto, setIdGasto] = useState(() => crypto.randomUUID());
  const [cuentas, setCuentas] = useState<Cuenta[] | null>(null);
  const [errorCuentas, setErrorCuentas] = useState('');
  /** Lo que no se pudo leer de un documento: se dice en la página, porque no hay formulario abierto. */
  const [avisoPagina, setAvisoPagina] = useState('');
  /** El error del formulario: se dice dentro del diálogo, una sola vez. */
  const [errorDialogo, setErrorDialogo] = useState('');
  const [anulando, setAnulando] = useState<GastoResumen | null>(null);
  const [motivo, setMotivo] = useState('');
  const [errorAnular, setErrorAnular] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [leyendo, setLeyendo] = useState(false);
  const [proveedores, setProveedores] = useState<TerceroEncontrado[]>([]);
  const [busquedaProveedor, setBusquedaProveedor] = useState('');
  const [abrirProveedor, setAbrirProveedor] = useState(false);
  const entradaArchivo = useRef<HTMLInputElement>(null);
  const entradaXml = useRef<HTMLInputElement>(null);
  const esPropietario = negocio?.rol === 'propietario';

  const cargar = useCallback(() => {
    if (!negocio) return;
    setErrorLista('');
    listarGastos(negocio.negocioId)
      .then(async (lista) => {
        const desde = lista.reduce((min, g) => (g.fecha < min ? g.fecha : min), fechaNegocio());
        setCerrados(await diasCerrados(negocio.negocioId, desde));
        setGastos(lista);
      })
      .catch((e) => setErrorLista(e instanceof Error ? e.message : 'Revisa la conexión.'));
  }, [negocio]);
  useEffect(cargar, [cargar]);

  // Las cuentas habilitadas, para preguntar por dónde salió la plata. La del mostrador va primero.
  const cargarCuentas = useCallback(() => {
    if (!negocio) return;
    setErrorCuentas('');
    listarCuentas(negocio.negocioId)
      .then(setCuentas)
      .catch((e) => setErrorCuentas(e instanceof Error ? e.message : 'Revisa la conexión.'));
  }, [negocio]);
  useEffect(cargarCuentas, [cargarCuentas]);

  // Los proveedores que coinciden con lo que se escribe, por nombre o por documento.
  const conBorrador = borrador !== null;
  useEffect(() => {
    if (!negocio || !conBorrador) return;
    const t = setTimeout(() => {
      buscarProveedores(negocio.negocioId, busquedaProveedor)
        .then(setProveedores)
        .catch(() => setProveedores([]));
    }, 250);
    return () => clearTimeout(t);
  }, [negocio, conBorrador, busquedaProveedor]);

  /**
   * El documento manda: si ya hay un tercero con ese número, su nombre entra puesto y no se edita aquí.
   * Si la búsqueda falla, el campo se libera: un nombre anterior no queda pegado a otro documento.
   */
  const docProveedor = borrador ? `${borrador.proveedorTipoDoc}|${borrador.nitEmisor}` : '';
  useEffect(() => {
    if (!negocio || !borrador) return;
    const [tipo, numero] = docProveedor.split('|');
    if (!numero || numero.replace(/\D/g, '').length < 6) {
      setBorrador((b) => (b && b.proveedorRegistrado ? { ...b, proveedorRegistrado: false, proveedorNombre: '' } : b));
      return;
    }
    const t = setTimeout(() => {
      void terceroPorDocumento(negocio.negocioId, tipo!, numero)
        .then((encontrado) =>
          setBorrador((b) =>
            !b
              ? b
              : encontrado
                ? { ...b, proveedorNombre: encontrado.nombre, proveedorRegistrado: true }
                : b.proveedorRegistrado
                  ? { ...b, proveedorNombre: '', proveedorRegistrado: false }
                  : b
          )
        )
        .catch(() => {
          setBorrador((b) => (b && b.proveedorRegistrado ? { ...b, proveedorNombre: '', proveedorRegistrado: false } : b));
          setErrorDialogo('No pudimos comprobar si ese proveedor ya está registrado. Revisa la conexión.');
        });
    }, 400);
    return () => clearTimeout(t);
    // Solo el documento dispara la búsqueda: el resto del borrador cambia mientras se escribe.
  }, [negocio, docProveedor]);

  /** Un borrador nuevo empieza con la búsqueda del proveedor limpia y su bloque como lo dejó el papel. */
  function abrirBorrador(b: Borrador, gastoId: string | null = null) {
    setBorrador(b);
    setCorrigiendo(gastoId);
    setIdGasto(crypto.randomUUID());
    setErrorDialogo('');
    setBusquedaProveedor('');
    setAbrirProveedor(false);
  }

  /** El XML es el original fiscal del documento: trae la razón social, que el QR no lleva. */
  async function alArchivoXml(archivo: File | undefined) {
    if (entradaXml.current) entradaXml.current.value = '';
    if (!archivo) return;
    setLeyendo(true);
    setAvisoPagina('');
    try {
      const lectura = await leerDocumentoDeArchivo(archivo);
      if (lectura.estado === 'rechazado') {
        setAvisoPagina(lectura.motivo);
        return;
      }
      const f = lectura.documento;
      abrirBorrador(
        conFilas(
          {
            ...BORRADOR_MANUAL,
            fuente: 'qr',
            cufe: f.cufe,
            nitEmisor: f.nitEmisor ?? '',
            proveedorNombre: f.razonSocialEmisor ?? '',
            fechaDocumento: f.fecha && /^\d{4}-\d{2}-\d{2}$/.test(f.fecha) ? f.fecha : null,
            subtotalDocumento: f.subtotal ?? null,
            total: f.total ?? null,
          },
          f.impuestos.map((i) => ({
            tipo: i.tipo,
            nombre: i.nombre ?? '',
            codigoDian: i.codigoDian,
            tarifa: i.tarifa,
            monto: i.monto,
          }))
        )
      );
    } finally {
      setLeyendo(false);
    }
  }

  function alQr(parseado: QrDian, crudo: string) {
    setEscaneando(false);
    setAvisoPagina('');
    if (parseado.tipo === 'no_dian') {
      abrirBorrador({
        ...BORRADOR_MANUAL,
        nota:
          'Ese QR no es de un documento electrónico del proveedor. Si te llegó por correo, sube el XML o ' +
          'el .zip; si no, completa los datos a mano.',
      });
      return;
    }
    const b = borradorDesdeQr(parseado, crudo);
    abrirBorrador(
      parseado.tipo === 'campos'
        ? b
        : {
            ...b,
            nota:
              'Ese QR solo trae la clave del documento. Si te llegó por correo, sube el XML o el .zip y sale ' +
              'todo, incluido el proveedor; si lo tienes en papel, completa los datos a mano.',
          }
    );
  }

  /** El documento del proveedor llega por correo en PDF o en foto: se busca su QR. El archivo no se guarda. */
  async function alArchivo(archivo: File | undefined) {
    if (!archivo) return;
    setLeyendo(true);
    setAvisoPagina('');
    try {
      const lectura = await leerQrDeArchivo(archivo);
      if (lectura.estado === 'rechazado') {
        setAvisoPagina(lectura.motivo);
        return;
      }
      if (lectura.estado === 'sin_qr') {
        abrirBorrador({
          ...BORRADOR_MANUAL,
          nota:
            'No encontramos un QR legible en el archivo. Si tienes el XML o el .zip del proveedor, súbelo y ' +
            'sale todo; si no, completa los datos a mano.',
        });
        return;
      }
      alQr(parsearQrDian(lectura.crudo), lectura.crudo);
    } finally {
      setLeyendo(false);
      if (entradaArchivo.current) entradaArchivo.current.value = '';
    }
  }

  async function guardar() {
    if (!negocio || !borrador) return;
    const impuestos = borrador.conImpuestos ? aImpuestos(borrador.impuestos) : [];
    const subtotal = subtotalDeGasto(borrador.total, impuestos);
    if (borrador.fuente === 'manual' && borrador.descripcion.trim() === '') {
      setErrorDialogo('Escribe qué se compró: es lo único que lo dice cuando no hay documento del proveedor.');
      return;
    }
    if (borrador.total === null || borrador.total <= 0 || subtotal === null) {
      setErrorDialogo('Escribe cuánto pagaste.');
      return;
    }
    if (impuestoSinNombre(impuestos)) {
      setErrorDialogo('Ponle nombre al impuesto que no es IVA ni INC: es lo único que dice cuál era.');
      return;
    }
    if (subtotal < 0) {
      setErrorDialogo('Los impuestos suman más que el total pagado.');
      return;
    }
    if (!borrador.origen) {
      setErrorDialogo('Elige de dónde salió la plata: una cuenta del negocio o la caja general.');
      return;
    }
    setOcupado(true);
    setErrorDialogo('');
    const cuentaId = borrador.origen === 'caja-general' ? null : borrador.origen;
    const cajaGeneral = borrador.origen === 'caja-general';
    const nit = borrador.nitEmisor || null;
    try {
      if (corrigiendo) {
        await editarGastoRpc({
          gastoId: corrigiendo,
          total: borrador.total,
          subtotal,
          categoria: borrador.categoria,
          descripcion: borrador.descripcion.trim() || null,
          cuentaId,
          cajaGeneral,
          impuestos,
          proveedorTipoDoc: nit ? borrador.proveedorTipoDoc : null,
          nitEmisor: nit,
          proveedorNombre: borrador.proveedorNombre || null,
        });
      } else {
        await registrarGastoRpc({
          negocioId: negocio.negocioId,
          uuidCliente: idGasto,
          fuente: borrador.fuente,
          total: borrador.total,
          descripcion: borrador.descripcion.trim() || null,
          cuentaId,
          cajaGeneral,
          categoria: borrador.categoria,
          cufe: borrador.cufe,
          qrCrudo: borrador.qrCrudo,
          nitEmisor: nit,
          proveedorTipoDoc: nit ? borrador.proveedorTipoDoc : null,
          proveedorNombre: borrador.proveedorNombre || null,
          fechaDocumento: borrador.fechaDocumento,
          subtotal,
          impuestos,
        });
      }
      setBorrador(null);
      cargar();
    } catch (e) {
      setErrorDialogo(e instanceof Error ? e.message : 'No se pudo guardar el gasto.');
    } finally {
      setOcupado(false);
    }
  }

  async function anular() {
    if (!anulando) return;
    if (motivo.trim().length < 3) {
      setErrorAnular('Para anular hay que escribir el motivo.');
      return;
    }
    setOcupado(true);
    setErrorAnular('');
    try {
      await anularGastoRpc(anulando.id, motivo.trim());
      setAnulando(null);
      cargar();
    } catch (e) {
      setErrorAnular(e instanceof Error ? e.message : 'No se pudo anular el gasto.');
    } finally {
      setOcupado(false);
    }
  }

  if (!negocio) return null;

  const impuestosVigentes = borrador?.conImpuestos ? aImpuestos(borrador.impuestos) : [];
  const subtotal = borrador ? subtotalDeGasto(borrador.total, impuestosVigentes) : null;
  const verProveedor = abrirProveedor || borrador?.fuente !== 'manual' || !!borrador?.nitEmisor;

  function ponerImpuesto(indice: number, parte: Partial<FilaImpuesto>) {
    setBorrador((b) =>
      !b ? b : { ...b, impuestos: b.impuestos.map((f, i) => (i === indice ? { ...f, ...parte } : f)) }
    );
  }

  /** Al marcar la casilla aparecen IVA e INC para escribirlos; los que ya traía el documento se quedan. */
  function marcarImpuestos(marcado: boolean) {
    setBorrador((b) => {
      if (!b) return b;
      const tiene = (t: TipoImpuestoGasto) => b.impuestos.some((f) => f.tipo === t);
      const filas = marcado
        ? [...(tiene('iva') ? [] : [FILA_VACIA('iva')]), ...(tiene('inc') ? [] : [FILA_VACIA('inc')]), ...b.impuestos]
        : b.impuestos;
      return { ...b, conImpuestos: marcado, impuestos: filas };
    });
  }

  /** «IVA», o «IVA 19%» cuando el documento trae la tarifa; el segundo del mismo tipo se distingue por número. */
  const etiquetaImpuesto = (f: FilaImpuesto) =>
    `${f.tipo === 'iva' ? 'IVA' : 'INC'}${f.tarifa !== null ? ` ${f.tarifa}%` : ''}`;

  const deDondeSalio = (g: GastoResumen) =>
    g.cajaGeneral ? 'Caja general' : g.cuentaEsEfectivo ? 'Caja del mostrador' : (g.cuenta ?? '—');

  return (
    <div className="space-y-4">
      <BarraDePagina titulo="Gastos">
        {esPropietario && (
          <div className="flex flex-wrap gap-2">
            <input
              ref={entradaArchivo}
              type="file"
              accept="application/pdf,image/png,image/jpeg,image/webp"
              className="hidden"
              onChange={(e) => void alArchivo(e.target.files?.[0])}
              data-e2e="gasto-archivo"
            />
            <input
              ref={entradaXml}
              type="file"
              accept=".xml,.zip,text/xml,application/xml,application/zip"
              className="hidden"
              onChange={(e) => void alArchivoXml(e.target.files?.[0])}
              data-e2e="gasto-archivo-xml"
            />
            <Boton onClick={() => entradaXml.current?.click()} disabled={leyendo} data-e2e="gasto-subir-xml">
              {leyendo ? 'Leyendo el documento…' : '🧾 Subir XML o ZIP'}
            </Boton>
            <Boton variante="secundario" onClick={() => entradaArchivo.current?.click()} disabled={leyendo} data-e2e="gasto-subir">
              📄 PDF o foto
            </Boton>
            <Boton variante="secundario" onClick={() => setEscaneando(true)} data-e2e="gasto-qr">📷 Escanear QR</Boton>
            <Boton variante="contorno" onClick={() => abrirBorrador({ ...BORRADOR_MANUAL })} data-e2e="gasto-manual">＋ Manual</Boton>
          </div>
        )}
      </BarraDePagina>
      {avisoPagina && <p className="text-warning bg-warning/10 rounded-control px-3 py-2 text-contenido font-semibold">{avisoPagina}</p>}

      <Tarjeta>
        {errorLista ? (
          <div className="flex flex-wrap items-center gap-3">
            <MensajeError e2e="gastos-error">⚠ No pudimos leer los gastos. {errorLista}</MensajeError>
            <Boton variante="secundario" onClick={cargar}>
              Reintentar
            </Boton>
          </div>
        ) : gastos === null ? (
          <Esqueleto lineas={6} />
        ) : gastos.length === 0 ? (
          <EstadoVacio
            titulo="Sin gastos registrados"
            detalle="Sube el XML que te manda el proveedor —o el .zip que lo trae— y sale todo, incluida su razón social. Del PDF o la foto leemos el QR: NIT, fecha y montos."
          />
        ) : (
          <TablaAncha>
            <EncabezadoTabla>
              <th className="py-2 pr-2">Pagado</th>
              <th className="py-2 pr-2">Descripción</th>
              <th className="py-2 pr-2">Proveedor</th>
              <th className="py-2 pr-2 text-right">Total</th>
              <th className="py-2 pr-2">De dónde salió</th>
              <th className="py-2 pr-2">Categoría</th>
              <th className="py-2 pr-2">Registrado por</th>
              {esPropietario && <ThAcciones />}
            </EncabezadoTabla>
            <tbody>
              {gastos.map((g) =>
                g.anuladoMotivo !== null ? (
                  <FilaTabla key={g.id} className="text-muted" data-e2e="gasto-fila-anulada">
                    <td className="py-2 pr-2">{formatearFecha(g.fecha)}</td>
                    <td className="py-2 pr-2" colSpan={2}>
                      <s>{g.descripcion ?? g.proveedor?.nombre ?? '—'}</s>{' '}
                      <span className="text-destructive font-semibold">ANULADO: «{g.anuladoMotivo}»</span>
                    </td>
                    <CeldaPlata>
                      <s>{formatearCOP(g.total)}</s>
                    </CeldaPlata>
                    <td className="py-2 pr-2" colSpan={esPropietario ? 4 : 3} />
                  </FilaTabla>
                ) : (
                  <FilaTabla key={g.id}>
                    <td className="py-2 pr-2">{formatearFecha(g.fecha)}</td>
                    <td className="py-2 pr-2">{g.descripcion ?? '—'}</td>
                    <td className="py-2 pr-2 font-semibold">{g.proveedor?.nombre ?? '—'}</td>
                    <CeldaPlata fuerte>{formatearCOP(g.total)}</CeldaPlata>
                    <td className="py-2 pr-2">{deDondeSalio(g)}</td>
                    <td className="py-2 pr-2">{CATEGORIAS.find((c) => c.valor === g.categoria)?.etiqueta}</td>
                    <td className="py-2 pr-2">
                      <RegistradoPor g={g} />
                    </td>
                    {esPropietario && (
                      <td className="py-2 text-right">
                        {cerrados.has(g.fecha) ? (
                          <span
                            className="text-muted whitespace-nowrap text-etiqueta"
                            title="Reábrelo en Cierre del día para corregirlo"
                            data-e2e="gasto-dia-cerrado"
                          >
                            🔒 día cerrado
                          </span>
                        ) : (
                          <span className="inline-flex gap-3">
                            <BotonEnlace onClick={() => abrirBorrador(borradorDeGasto(g), g.id)} data-e2e="gasto-editar">
                              Editar
                            </BotonEnlace>
                            <BotonEnlace
                              tono="peligro"
                              onClick={() => {
                                setAnulando(g);
                                setMotivo('');
                                setErrorAnular('');
                              }}
                              data-e2e="gasto-anular"
                            >
                              Anular
                            </BotonEnlace>
                          </span>
                        )}
                      </td>
                    )}
                  </FilaTabla>
                )
              )}
            </tbody>
          </TablaAncha>
        )}
      </Tarjeta>

      <Modal abierto={escaneando} titulo="Escanear QR del proveedor" onCerrar={() => setEscaneando(false)}>
        <EscanerQr onQr={alQr} />
      </Modal>

      <Modal abierto={anulando !== null} titulo="Anular gasto" onCerrar={() => setAnulando(null)}>
        {anulando && (
          <div className="space-y-4">
            <p className="text-contenido">
              {anulando.descripcion ?? anulando.proveedor?.nombre ?? 'Gasto'} · {formatearCOP(anulando.total)} ·{' '}
              {formatearFecha(anulando.fecha)}. Queda registrado con su motivo y deja de sumar.
            </p>
            <Campo
              etiqueta="Motivo *"
              value={motivo}
              placeholder="se registró dos veces"
              onChange={(e) => setMotivo(e.target.value)}
              data-e2e="gasto-anular-motivo"
            />
            <MensajeError e2e="gasto-anular-error">{errorAnular}</MensajeError>
            <FilaAcciones>
              <Boton variante="contorno" onClick={() => setAnulando(null)} disabled={ocupado}>Volver</Boton>
              <Boton variante="peligro" onClick={() => void anular()} disabled={ocupado} data-e2e="gasto-anular-confirmar">
                {ocupado ? 'Anulando…' : 'Anular gasto'}
              </Boton>
            </FilaAcciones>
          </div>
        )}
      </Modal>

      <Modal
        abierto={borrador !== null}
        titulo={corrigiendo ? 'Corregir gasto' : 'Registrar gasto'}
        onCerrar={() => setBorrador(null)}
      >
        {borrador && (
          <div className="space-y-4">
            {borrador.nota && (
              <p className="bg-info/10 rounded-control px-3 py-2 text-etiqueta font-semibold" data-e2e="gasto-nota">
                {borrador.nota}
              </p>
            )}
            {borrador.cufe && (
              <div className="bg-success/10 rounded-control px-3 py-2 text-etiqueta">
                <p className="num text-success break-all" data-e2e="gasto-cufe">
                  Documento DIAN: {borrador.cufe.slice(0, 24)}…
                </p>
                {/* El portal bloquea a los programas, así que la consulta se abre a mano y ahí no hay
                    nada que resolver. Es el camino de un documento en papel, sin XML del que leer. */}
                <a
                  href={urlConsultaDian(borrador.cufe)}
                  target="_blank"
                  rel="noreferrer"
                  className="text-info underline"
                  data-e2e="gasto-ver-dian"
                >
                  Consultarlo en la DIAN para copiar sus datos
                </a>
              </div>
            )}

            <Bloque numero={1} titulo={borrador.fuente === 'manual' ? '¿Qué fue? *' : '¿Qué fue?'}>
              <FilaCampos>
                <Campo
                  etiqueta="Descripción"
                  value={borrador.descripcion}
                  placeholder="3 canastas de fresa"
                  onChange={(e) => setBorrador({ ...borrador, descripcion: e.target.value })}
                  data-e2e="gasto-descripcion"
                />
                <CampoLista
                  etiqueta="Categoría"
                  value={borrador.categoria}
                  onChange={(e) => setBorrador({ ...borrador, categoria: e.target.value as CategoriaGasto })}
                  data-e2e="gasto-categoria"
                >
                  {CATEGORIAS.map((c) => (
                    <option key={c.valor} value={c.valor}>{c.etiqueta}</option>
                  ))}
                </CampoLista>
              </FilaCampos>
            </Bloque>

            <Bloque numero={2} titulo="¿Cuánto pagaste? *">
              <FilaCampos>
                <CampoNumero
                  etiqueta="Total pagado"
                  plata
                  min={0}
                  valor={borrador.total}
                  onValor={(n) => setBorrador({ ...borrador, total: n })}
                  data-e2e="gasto-total"
                />
              </FilaCampos>
              <label className="flex items-center gap-2 text-contenido font-semibold">
                <input
                  type="checkbox"
                  checked={borrador.conImpuestos}
                  onChange={(e) => marcarImpuestos(e.target.checked)}
                  data-e2e="gasto-con-impuestos"
                />
                El recibo trae IVA o INC
              </label>
              {borrador.conImpuestos && (
                <div className="space-y-2 pl-6">
                  <div className="flex flex-wrap items-end gap-3">
                    {borrador.impuestos.map((f, i) =>
                      f.tipo === 'otro' ? null : (
                        <div key={i} className="w-40">
                          <CampoNumero
                            etiqueta={etiquetaImpuesto(f)}
                            plata
                            min={0}
                            valor={f.monto}
                            placeholder="$ 0"
                            onValor={(n) => ponerImpuesto(i, { monto: n })}
                            data-e2e={`gasto-${f.tipo}${borrador.impuestos.slice(0, i).some((o) => o.tipo === f.tipo) ? `-${i}` : ''}`}
                          />
                        </div>
                      )
                    )}
                  </div>
                  {borrador.impuestos.map((f, i) =>
                    f.tipo !== 'otro' ? null : (
                      <div key={i} className="grid grid-cols-[1fr_9rem_3rem] items-end gap-2">
                        <Campo
                          etiqueta="Otro impuesto"
                          value={f.nombre}
                          placeholder="Bolsas, alumbrado…"
                          onChange={(e) => ponerImpuesto(i, { nombre: e.target.value })}
                          data-e2e={`gasto-otro-nombre-${i}`}
                        />
                        <CampoNumero
                          etiqueta="Monto"
                          plata
                          min={0}
                          valor={f.monto}
                          onValor={(n) => ponerImpuesto(i, { monto: n })}
                          data-e2e={`gasto-otro-monto-${i}`}
                        />
                        <Boton
                          variante="contorno"
                          onClick={() =>
                            setBorrador({ ...borrador, impuestos: borrador.impuestos.filter((_, j) => j !== i) })
                          }
                          data-e2e={`gasto-otro-quitar-${i}`}
                        >
                          ✕
                        </Boton>
                      </div>
                    )
                  )}
                  <div className="flex flex-wrap items-center gap-3">
                    <Boton
                      variante="contorno"
                      onClick={() => setBorrador({ ...borrador, impuestos: [...borrador.impuestos, FILA_VACIA('otro')] })}
                      data-e2e="gasto-otro-agregar"
                    >
                      ＋ Otro impuesto
                    </Boton>
                    <span className="text-muted text-contenido" data-e2e="gasto-subtotal">
                      (Subtotal: {subtotal === null ? '—' : formatearCOP(subtotal)}, lo calcula la pantalla)
                    </span>
                  </div>
                </div>
              )}
              {borrador.subtotalDocumento !== null && subtotal !== null && borrador.subtotalDocumento !== subtotal && (
                <p
                  className="text-warning bg-warning/10 rounded-control px-3 py-2 text-etiqueta font-semibold"
                  data-e2e="gasto-descuadre"
                >
                  El documento dice {formatearCOP(borrador.subtotalDocumento)} antes de impuestos y el total menos los
                  impuestos da {formatearCOP(subtotal)}. Revisa los impuestos.
                </p>
              )}
            </Bloque>

            {/* Sin esto el cuadre no puede restar el gasto, y «traslado de dinero» vuelve a ser un
                número que alguien escribe a mano. La caja general no la cuenta el arqueo. */}
            <Bloque numero={3} titulo="¿De dónde salió la plata? *">
              {errorCuentas ? (
                <div className="flex flex-wrap items-center gap-3">
                  <MensajeError e2e="gasto-cuentas-error">
                    ⚠ No pudimos leer las cuentas del negocio. {errorCuentas}
                  </MensajeError>
                  <Boton variante="secundario" onClick={cargarCuentas}>
                    Reintentar
                  </Boton>
                </div>
              ) : (
                <CampoLista
                  value={borrador.origen}
                  onChange={(e) => setBorrador({ ...borrador, origen: e.target.value })}
                  data-e2e="gasto-origen"
                >
                  <option value="">Elige de dónde salió</option>
                  {(cuentas ?? []).map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.esEfectivo ? 'Caja del mostrador' : c.nombre}
                    </option>
                  ))}
                  <option value="caja-general">Caja general</option>
                </CampoLista>
              )}
            </Bloque>

            <Bloque numero={4} titulo="Proveedor (opcional)">
              {!verProveedor ? (
                <Boton variante="contorno" onClick={() => setAbrirProveedor(true)} data-e2e="gasto-proveedor-abrir">
                  Anotar el proveedor
                </Boton>
              ) : (
                <div className="space-y-2">
                  <Campo
                    etiqueta="Buscar entre tus proveedores"
                    value={busquedaProveedor}
                    placeholder="Por nombre o por documento"
                    onChange={(e) => setBusquedaProveedor(e.target.value)}
                    data-e2e="gasto-proveedor-buscar"
                  />
                  {busquedaProveedor.trim() !== '' && proveedores.length > 0 && (
                    <ul className="border-border rounded-control divide-border max-h-40 divide-y overflow-y-auto border">
                      {proveedores.map((pr) => (
                        <li key={pr.id}>
                          <button
                            className="hover:bg-surface w-full px-3 py-2 text-left text-contenido"
                            onClick={() => {
                              setBorrador({
                                ...borrador,
                                proveedorTipoDoc: pr.tipoDocumento,
                                nitEmisor: pr.numeroDocumento,
                                proveedorNombre: pr.nombre,
                                proveedorRegistrado: true,
                              });
                              setBusquedaProveedor('');
                            }}
                            data-e2e="gasto-proveedor-resultado"
                          >
                            {pr.nombre} · {pr.numeroDocumento}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                  <CampoDocumento
                    contexto="proveedor"
                    valor={{ tipo: borrador.proveedorTipoDoc, numero: borrador.nitEmisor }}
                    onCambio={(d) => setBorrador({ ...borrador, proveedorTipoDoc: d.tipo, nitEmisor: d.numero })}
                    etiquetaTipo="Documento del proveedor"
                    e2eTipo="gasto-proveedor-tipo"
                    e2eNumero="gasto-nit"
                  />
                  <Campo
                    etiqueta={borrador.proveedorRegistrado ? 'Proveedor ya registrado' : 'Nombre o razón social'}
                    value={borrador.proveedorNombre}
                    readOnly={borrador.proveedorRegistrado}
                    ayuda={
                      borrador.proveedorRegistrado
                        ? 'Se llenó desde tus terceros. Para corregirlo, entra a Terceros.'
                        : undefined
                    }
                    onChange={(e) => setBorrador({ ...borrador, proveedorNombre: e.target.value })}
                    data-e2e="gasto-proveedor-nombre"
                  />
                </div>
              )}
            </Bloque>

            <MensajeError e2e="gasto-error">{errorDialogo}</MensajeError>
            <FilaAcciones>
              <Boton variante="contorno" onClick={() => setBorrador(null)} disabled={ocupado}>Volver</Boton>
              <Boton
                onClick={() => void guardar()}
                disabled={ocupado || errorCuentas !== '' || cuentas === null}
                data-e2e="gasto-guardar"
              >
                {ocupado ? 'Guardando…' : corrigiendo ? 'Guardar cambios' : 'Registrar gasto'}
              </Boton>
            </FilaAcciones>
          </div>
        )}
      </Modal>
    </div>
  );
}
